import React, { useEffect, useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createListingSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  ListingCard,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  InlineTextButton,
  PrimaryButton,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import FlashBodyPartModal from '../../components/OrderPanel/FlashBodyPartModal/FlashBodyPartModal';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  initiateQuery,
  initiateFlashBooking,
  generateAndUpdateShareCode,
  fetchPortfolioListingForFlash,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  getListingPageSchema,
  handleInitiateQuery,
  handleFlashBookSession,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import ArtistCard from './ArtistCard';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import {
  isFlashListing,
  getFlashSpecs,
  getFlashPriceInSubunits,
  formatFlashCurrencyFromSubunits,
  formatFlashNumber,
  PORTFOLIO_LISTING_TYPE,
} from '../../util/flashHelpers';
import ShareLinkBarMayBe from './ShareLinkBarMayBe.js';
import SectionSocialMediaMaybe from './SectionSocialMediaMaybe.js';
import { getUserPlan } from '../../util/getUserPlan.js';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { createResourceLocatorString } from '../../util/routes.js';
import { urlAlphabet } from 'nanoid';
import { ReactComponent as InfoIcon } from '../../assets/svgs/infoIcon.svg';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [flashBodyPartModalOpen, setFlashBodyPartModalOpen] = useState(false);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitiateInquiry,
    onInitiateFlashBooking,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onGenerateAndUpdateShareCode,
    linkGenerateInProgress,
    linkGenerateError,
    generatedShortCode,
    portfolioListingForFlash,
    portfolioListingForFlashFetchInProgress,
    authorFlashListings,
    isProvider,
  } = props;

  const searchParams = parse(location.search);

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  // console.log(currentListing, listingId, ensureListing(getListing(listingId)));

  useEffect(() => {
    if (searchParams?.sh && typeof window !== 'undefined') {
      const listingId = rawParams.id;
      const presentData = window.sessionStorage.getItem(listingId);
      const currentTime = Date.now();
      const timeDiff = currentTime - JSON.parse(presentData)?.v_d || 0;
      const timeDiffInMinutes = Math.floor(timeDiff / 1000 / 60);

      const shareCode = currentListing?.attributes?.publicData?.shareCode;

      const isCorrectCode = shareCode === searchParams?.sh;

      if (!presentData || (timeDiffInMinutes > 60 && isCorrectCode)) {
        window.sessionStorage.setItem(
          listingId,
          JSON.stringify({ c: searchParams?.sh, v_d: currentTime })
        );
      }
    }
  }, [searchParams]);

  const preferredListingSlug = createListingSlug(currentListing);
  const hasFlashSlug = currentListing.attributes.publicData?.listingType === 'flash_tattoo';
  const listingSlug = hasFlashSlug ? preferredListingSlug : rawParams.slug || preferredListingSlug;
  const params = { slug: listingSlug, ...rawParams };
  const canonicalPath =
    currentListing.id && hasFlashSlug
      ? createResourceLocatorString(
          'ListingPage',
          routeConfiguration,
          { id: currentListing.id.uuid, slug: preferredListingSlug },
          {}
        )
      : null;

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  if (!rawParams.slug && currentListing.id) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { hasActivePlan, plan } = getUserPlan(ensuredAuthor);

  const { formattedPrice } = priceData(price, config.currency, intl);

  // ── Flash-specific derived data ──────────────────────────────────────────────
  const isFlash = isFlashListing(currentListing);
  // Providers (userType: tatuador-id) shouldn't be able to book sessions, mirrors TattoInquiryForm.
  const flashBookSessionDisabled = sendInquiryInProgress || (isAuthenticated && isProvider);

  // Reservation fee = 10% deposit + fixed service charge (mirrors server-side logic)
  const flashPriceInSubunits = isFlash ? getFlashPriceInSubunits(currentListing) : null;
  const flashServiceFeeInSubunits =
    (process.env.REACT_APP_CUSTOMER_FIXED_SERVICE_CHARGE &&
      Number(process.env.REACT_APP_CUSTOMER_FIXED_SERVICE_CHARGE)) ||
    2000;
  const flashDepositInSubunits =
    flashPriceInSubunits != null ? Math.round(flashPriceInSubunits * 0.1) : null;
  const flashReservationFeeInSubunits =
    flashDepositInSubunits != null ? flashDepositInSubunits + flashServiceFeeInSubunits : null;
  // Format flash price + reservation fee consistently in Brazilian currency
  // (R$ 1.000,00) — same helpers used on the flash summary and checkout.
  // Flash prices are always in BRL regardless of the marketplace's default
  // `config.currency` (e.g. USD). TransactionPanel's FlashTattooSummary uses
  // the listing's own price currency with a BRL fallback — match that here
  // instead of using config.currency directly, or the currency symbol (not
  // the comma/dot punctuation) ends up wrong, e.g. "US$ 1.000,00".
  const flashCurrency = currentListing?.attributes?.price?.currency || 'BRL';
  const formattedFlashPrice = formatFlashCurrencyFromSubunits(flashPriceInSubunits, flashCurrency);
  const formattedFlashReservationFee = formatFlashCurrencyFromSubunits(
    flashReservationFeeInSubunits,
    flashCurrency
  );

  // Tattoo styles for the left-panel flash info card
  const tattooStyleFieldConfig = listingConfig.listingFields?.find(
    f => f.key === 'tattoostylefield_id'
  );
  const tattooStyleEnumOptions = tattooStyleFieldConfig?.enumOptions || [];
  const selectedTattooStyleKeys = publicData.tattoostylefield_id || [];
  const selectedTattooStyleLabels = selectedTattooStyleKeys.map(key => {
    const opt = tattooStyleEnumOptions.find(o => o.option === key);
    return opt ? opt.label : key;
  });

  // Years of experience fetched from the artist's portfolio listing
  const yearsOfExperience =
    portfolioListingForFlash?.attributes?.publicData?.experiencefield_id;

  // ────────────────────────────────────────────────────────────────────────────

  // ── Portfolio page: preview of this artist's flash designs ──────────────────
  // arrives here is already trimmed — an artist with fewer just renders fewer.
  const isPortfolioListing = listingType === PORTFOLIO_LISTING_TYPE;
  const showFlashPreview = isPortfolioListing && authorFlashListings.length > 0;
  // ────────────────────────────────────────────────────────────────────────────

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const onSubmitInitiateInquiry = handleInitiateQuery({
    ...commonParams,
    getListing,
    onInitiateInquiry,
    listingId,
  });

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    listingId,
  });

  const { requireAuth: requireAuthForFlashBooking, confirmBooking: confirmFlashBooking } =
    handleFlashBookSession({
      ...commonParams,
      getListing,
      onInitiateFlashBooking,
      listingId,
      currentUser,
      location,
    });

  const onFlashBookSession = () => {
    if (requireAuthForFlashBooking()) {
      setFlashBodyPartModalOpen(true);
    }
  };

  const handleFlashBodyPartConfirm = bodyPartSelection => {
    setFlashBodyPartModalOpen(false);
    confirmFlashBooking(bodyPartSelection);
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const handleBack = e => {
    e.preventDefault();
    if (history.length > 1) {
      history.goBack();
      return;
    }
    const url = createResourceLocatorString('LandingPage', routeConfiguration);
    history.replace(url);
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  // Flash specs for the left-panel info card
  const { flashSize, flashDuration, flashPrice } = isFlash
    ? getFlashSpecs(currentListing)
    : { flashSize: {}, flashDuration: null, flashPrice: null };
  const flashHasSize = flashSize?.width && flashSize?.height;
  const flashHasPrice = flashPrice != null && flashPrice !== '';
  const flashHasDuration = flashDuration != null && flashDuration !== '';
  const { schemaTitle, schema } = getListingPageSchema({
    listing: currentListing,
    title,
    description,
    price,
    formattedPrice,
    location,
    productURL: canonicalPath ? `${config.marketplaceRootURL}${canonicalPath}` : undefined,
    config,
    intl,
  });

  return (
    <Page
      title={schemaTitle}
      canonicalPath={canonicalPath}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={schema}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.productGalleryControls}>
            {/* {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null} */}

            <div className={css.backBtnContainer}>
              <InlineTextButton onClick={handleBack} type="button" className={css.backBtn}>
                <IoIosArrowRoundBack size={24} />
                {intl.formatMessage({ id: 'back' })}
              </InlineTextButton>
            </div>
            <div className={css.postImageGalleryDiv}>
              {currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  currentUser={currentUser}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}

              {isOwnListing && (
                <div className={css.extraSentences}>
                  <p>
                    <strong>
                      <FormattedMessage id="ListingPageCarousel.extraSentences.firstParaStrong" />
                    </strong>{' '}
                    <FormattedMessage id="ListingPageCarousel.extraSentences.firstPara" />
                  </p>
                  <p>
                    <FormattedMessage id="ListingPageCarousel.extraSentences.secondParaA" />{' '}
                    <strong>
                      <FormattedMessage id="ListingPageCarousel.extraSentences.secondParaStrong" />
                    </strong>{' '}
                    <FormattedMessage id="ListingPageCarousel.extraSentences.secondParaB" />
                  </p>
                </div>
              )}

              {currentListing.id && isOwnListing ? (
                <ShareLinkBarMayBe
                  className={css.actionBarForProductLayout}
                  listing={currentListing}
                  currentUser={currentUser}
                  id={listingId.uuid}
                  onGenerateAndUpdateShareCode={onGenerateAndUpdateShareCode}
                  config={config}
                  linkGenerateInProgress={linkGenerateInProgress}
                  linkGenerateError={linkGenerateError}
                  generatedShortCode={generatedShortCode}
                  history={history}
                />
              ) : null}
            </div>
          </div>

          <div className={css.mainColumnForProductLayout}>
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
              hasActivePlan={hasActivePlan}
              isFlash={isFlash}
            />

            {!isFlash && (
              <div className={css.mobileHeading}>
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              </div>
            )}

            {showFlashPreview && (
              <section className={css.flashPreviewSection}>
                <h2 className={css.flashPreviewTitle}>
                  <FormattedMessage id="ListingPage.flashPreviewTitle" />
                </h2>
                <p className={css.flashPreviewIntro}>
                  <FormattedMessage id="ListingPage.flashPreviewIntroQuestion" />
                  <br />
                  <FormattedMessage id="ListingPage.flashPreviewIntroInvite" />
                </p>

                <div className={css.flashPreviewGrid}>
                  {authorFlashListings.map(l => (
                    <ListingCard
                      key={l.id.uuid}
                      className={css.flashPreviewCard}
                      listing={l}
                      showAuthorInfo
                    />
                  ))}
                </div>

                {ensuredAuthor.id && (
                  <NamedLink
                    className={css.flashPreviewAllLink}
                    name="ArtistProfilePage"
                    params={{ id: ensuredAuthor.id.uuid, tab: 'flash' }}
                  >
                    <FormattedMessage id="ListingPage.flashPreviewSeeAll" />
                    <span aria-hidden="true"> →</span>
                  </NamedLink>
                )}
              </section>
            )}

            <SectionTextMaybe text={description} showAsIngress />

            {/* For flash listings: consolidated info card with styles, experience, and specs.
                For other listings: standard custom listing fields. */}
            {isFlash ? (
              <div className={css.flashLeftInfoCard}>
                {title && (
                  <div className={css.flashLeftInfoRow}>
                    <span className={css.flashLeftInfoLabel}>
                      <FormattedMessage id="ListingPage.flashTitleLabel" />
                    </span>
                    <span className={css.flashLeftInfoTitle}>{title}</span>
                  </div>
                )}

                {selectedTattooStyleLabels.length > 0 && (
                  <div className={css.flashLeftInfoRow}>
                    <span className={css.flashLeftInfoLabel}>
                      <FormattedMessage id="ListingPage.flashTattooStyles" />
                    </span>
                    <div className={css.flashStyleTags}>
                      {selectedTattooStyleLabels.map((label, i) => (
                        <span key={i} className={css.flashStyleTag}>{label}</span>
                      ))}
                    </div>
                  </div>
                )}

                {(flashHasSize || flashHasPrice || flashHasDuration) && (
                  <div className={css.flashDetailsGrid}>
                    {flashHasSize && (
                      <div className={css.flashDetailItem}>
                        <span className={css.flashDetailLabel}>
                          <FormattedMessage id="ListingPage.flashDimensions" />
                        </span>
                        <span className={css.flashDetailValue}>
                          <FormattedMessage
                            id="ListingPage.flashDimensionValue"
                            values={{
                              width: flashSize.width,
                              height: flashSize.height,
                              unit: flashSize.unit || 'cm',
                            }}
                          />
                        </span>
                      </div>
                    )}
                    {flashHasDuration && (
                      <div className={css.flashDetailItem}>
                        <span className={css.flashDetailLabel}>
                          <FormattedMessage id="ListingPage.flashDuration" />
                        </span>
                        <span className={css.flashDetailValue}>
                          <FormattedMessage
                            id="ListingPage.flashDurationValue"
                            values={{ hours: Number(flashDuration) }}
                          />
                        </span>
                      </div>
                    )}
                    {flashHasPrice && (
                      <div className={css.flashDetailItem}>
                        <span className={css.flashDetailLabel}>
                          <FormattedMessage id="ListingPage.flashPrice" />
                        </span>
                        <span className={css.flashDetailValue}>
                          <FormattedMessage
                            id="ListingPage.flashPriceValue"
                            values={{ price: formatFlashNumber(flashPrice) }}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {yearsOfExperience && (
                  <div className={css.flashLeftInfoRow}>
                    <span className={css.flashLeftInfoLabel}>
                      <FormattedMessage id="ListingPage.flashYearsExperience" />
                    </span>
                    <span className={css.flashLeftInfoValue}><FormattedMessage id="ListingPage.yearsExperienceValue" values={{ years: yearsOfExperience }} /></span>
                  </div>
                )}
              </div>
            ) : (
              <CustomListingFields
                publicData={publicData}
                metadata={metadata}
                listingFieldConfigs={listingConfig.listingFields}
                categoryConfiguration={config.categoryConfiguration}
                intl={intl}
              />
            )}

            {hasActivePlan && plan === 'pro' && (
              <SectionSocialMediaMaybe
                listing={currentListing}
                heading={intl.formatMessage({ id: 'listingPageSocialMediaPanel.title' })}
              />
            )}

            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />

            {/* Author section: only show at bottom for non-flash listings.
                For flash listings it appears in the right-panel Card 3. */}
            {!isFlash && (
              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            )}
          </div>

          <div className={css.orderColumnForProductLayout}>
            {isFlash && !isOwnListing ? (
              <div className={css.flashRightPanel}>
                {/* Card 1: Price + reservation fee + CTA */}
                <div className={css.flashPriceCard}>
                  {formattedFlashPrice && (
                    <div className={css.flashPriceAmount}>{formattedFlashPrice}</div>
                  )}
                  {formattedFlashReservationFee && (
                    <div className={css.flashReservationFee}>
                      <FormattedMessage
                        id="ListingPage.flashReservationDeposit"
                        values={{ amount: formattedFlashReservationFee }}
                      />
                    </div>
                  )}
                  <PrimaryButton
                    className={css.flashScheduleBtn}
                    onClick={onFlashBookSession}
                    inProgress={sendInquiryInProgress}
                    disabled={flashBookSessionDisabled}
                  >
                    <FormattedMessage id="ListingPage.flashScheduleSession" />
                  </PrimaryButton>
                  {!!isProvider && (
                    <div className={css.flashProviderText}>
                      <InfoIcon className={css.flashProviderIcon} />
                      <FormattedMessage id="TattooInquiryForm.ProviderText" />
                    </div>
                  )}
                </div>

                {/* Card 2: Important information */}
                <div className={css.flashInfoCard}>
                  <p className={css.flashInfoCardTitle}>
                    <FormattedMessage id="ListingPage.flashImportantInfo" />
                  </p>
                  <ul className={css.flashInfoList}>
                    <li><FormattedMessage id="ListingPage.flashInfoExclusive" /></li>
                    <li><FormattedMessage id="ListingPage.flashInfoScheduling" /></li>
                    <li><FormattedMessage id="ListingPage.flashInfoCheckDate" /></li>
                  </ul>
                </div>
              </div>
            ) : (
              <OrderPanel
                className={css.productOrderPanel}
                listing={currentListing}
                isOwnListing={isOwnListing}
                onSubmit={handleOrderSubmit}
                authorLink={
                  <NamedLink
                    className={css.authorNameLink}
                    name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                    params={params}
                    to={{ hash: '#author' }}
                  >
                    {authorDisplayName}
                  </NamedLink>
                }
                title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
                titleDesktop={
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                }
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onContactUser={onContactUser}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                marketplaceName={config.marketplaceName}
                onSubmitInitiateInquiry={onSubmitInitiateInquiry}
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                portfolioListingForFlash={portfolioListingForFlash}
                portfolioListingForFlashFetchInProgress={portfolioListingForFlashFetchInProgress}
                onFlashBookSession={onFlashBookSession}
              />
            )}
          </div>
        </div>

        {/* Full-width "About the artist" section for flash listings, below the two-column content */}
        {isFlash && !isOwnListing && (
          <div className={css.flashArtistSectionFullWidth}>
            <ArtistCard author={currentListing.author} authorDisplayName={authorDisplayName} />
          </div>
        )}

        {/* Mobile-only sticky bar for flash listings (Card 1 is hidden on mobile via CSS) */}
        {isFlash && !isOwnListing && (
          <div className={css.flashMobileBookBar}>
            <div className={css.flashMobileBookRow}>
              {formattedFlashReservationFee && (
                <div className={css.flashMobileDepositInfo}>
                  <div className={css.flashMobilePriceAmount}>{formattedFlashPrice}</div>
                  <span className={css.flashMobileDepositLabel}>
                    <FormattedMessage id="ListingPage.flashReservationDepositLabel" />
                  </span>
                  <span className={css.flashMobileDepositAmount}>{formattedFlashReservationFee}</span>
                </div>
              )}
              <PrimaryButton
                className={css.flashMobileBookBtn}
                onClick={onFlashBookSession}
                inProgress={sendInquiryInProgress}
                disabled={flashBookSessionDisabled}
              >
                <FormattedMessage id="ListingPage.flashScheduleSession" />
              </PrimaryButton>
            </div>
            {!!isProvider && (
              <div className={css.flashProviderText}>
                <InfoIcon className={css.flashProviderIcon} />
                <FormattedMessage id="TattooInquiryForm.ProviderText" />
              </div>
            )}
          </div>
        )}

        {isFlash && !isOwnListing && (
          <FlashBodyPartModal
            isOpen={flashBodyPartModalOpen}
            onClose={() => setFlashBodyPartModalOpen(false)}
            onConfirm={handleFlashBodyPartConfirm}
            onManageDisableScrolling={onManageDisableScrolling}
            submitInProgress={sendInquiryInProgress}
          />
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  isProvider: null,
  authorFlashListings: [],
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  isProvider: bool,
  authorFlashListings: arrayOf(propTypes.listing),
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    linkGenerateInProgress,
    linkGenerateError,
    generatedShortCode,
    portfolioListingForFlash,
    portfolioListingForFlashFetchInProgress,
    authorFlashListingRefs,
  } = state.ListingPage;
  const { currentUser, isProvider } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    isProvider,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    linkGenerateInProgress,
    linkGenerateError,
    generatedShortCode,
    portfolioListingForFlash,
    portfolioListingForFlashFetchInProgress,
    authorFlashListings: getMarketplaceEntities(state, authorFlashListingRefs),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitiateInquiry: (listing, requirements, extraData, customerPhoneNumber) =>
    dispatch(initiateQuery(listing, requirements, extraData, customerPhoneNumber)),
  onInitiateFlashBooking: (listing, bodyPartSelection) =>
    dispatch(initiateFlashBooking(listing, bodyPartSelection)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onGenerateAndUpdateShareCode: (listingId, config) =>
    dispatch(generateAndUpdateShareCode(listingId, config)),
  onFetchPortfolioListingForFlash: (authorId, config) =>
    dispatch(fetchPortfolioListingForFlash(authorId, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
